'use client';

import { useMemo } from 'react';
import { useReactiveAuthTokens } from '@/app/contexts';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';

/**
 * Session inputs every direct-CORS cell-service hook needs:
 *
 * - ``cellId`` — decoded from ``AuthContext.idToken``'s ``custom:cell_id``
 *   claim. Used both to build the per-cell URL and as part of TanStack
 *   query keys.
 * - ``accessToken`` — the **real** Cognito access token
 *   (``token_use: "access"``) from ``AuthContext.realAccessToken``.
 *   ``AuthContext.accessToken`` is NOT used here: it is the ID token in this
 *   app for legacy BFF cookie reasons and would be rejected by per-service
 *   Lambda Authorizers.
 * - ``enabled`` — convenience boolean for ``useQuery`` / ``useInfiniteQuery``;
 *   queries stay disabled until both pieces are present.
 *
 * Shared by every direct-CORS service client (provenance-service,
 * junkdrawer-service, …) so auth wiring stays consistent.
 */
export function useCellServiceSession() {
  const { idToken, realAccessToken } = useReactiveAuthTokens();
  const cellId = useMemo(() => resolveCellIdFromIdToken(idToken), [idToken]);
  return {
    cellId,
    accessToken: realAccessToken,
    enabled: Boolean(cellId) && Boolean(realAccessToken),
  };
}
